import { HTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '../../lib/utils';

// Table Container
const TableContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('table-container', className)} {...props} />
  )
);
TableContainer.displayName = 'TableContainer';

// Table
const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn('table', className)} {...props} />
  )
);
Table.displayName = 'Table';

// Table Header
const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('', className)} {...props} />
  )
);
TableHeader.displayName = 'TableHeader';

// Table Body
const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn('', className)} {...props} />
  )
);
TableBody.displayName = 'TableBody';

// Table Row
const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr ref={ref} className={cn('transition-colors', className)} {...props} />
  )
);
TableRow.displayName = 'TableRow';

// Table Head Cell
const TableHead = forwardRef<HTMLTableCellElement, HTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th ref={ref} className={cn('', className)} {...props} />
  )
);
TableHead.displayName = 'TableHead';

// Table Cell
interface TableCellProps extends HTMLAttributes<HTMLTableCellElement> {
  colSpan?: number;
}

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, colSpan, ...props }, ref) => (
    <td ref={ref} className={cn('', className)} colSpan={colSpan} {...props} />
  )
);
TableCell.displayName = 'TableCell';

// Empty State
interface TableEmptyProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}

const TableEmpty = ({ icon, title, description, action }: TableEmptyProps) => (
  <TableRow>
    <TableCell colSpan={100} className="py-12">
      <div className="flex flex-col items-center justify-center text-center">
        {icon && (
          <div className="w-12 h-12 rounded-full bg-surface-800 flex items-center justify-center mb-4 text-surface-500">
            {icon}
          </div>
        )}
        <h3 className="text-lg font-medium text-surface-300">{title}</h3>
        {description && (
          <p className="mt-1 text-sm text-surface-500 max-w-sm">{description}</p>
        )}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </TableCell>
  </TableRow>
);

// Loading State
const TableLoading = ({ columns = 5, rows = 5 }: { columns?: number; rows?: number }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <TableRow key={rowIndex}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <TableCell key={colIndex}>
            <div className="skeleton h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

export {
  TableContainer,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  TableLoading,
};
